import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useEffect, useMemo, useState } from 'react';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { sansCadratin } from '../utils/richText';
import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import { getScenarioForMission } from '../data/scenarios';
import CodeBlock from '../components/CodeBlock';
import DiagramViewer from '../components/DiagramViewer';
import ElementHeader from '../components/ElementHeader';
import HeaderNav from '../components/HeaderNav';
import { useElementVisited } from '../utils/elementProgress';
import { useVisitedItemNav } from '../hooks/useVisitedItemNav';
import { useMissionScenario } from '../hooks/useMissionScenario';
import { TYPE_COLOR, glow, glowText, type Palette } from '../utils/cyb';
import { usePalette } from '../hooks/usePalette';
import { useT } from '../i18n/useT';
import type { MessageKey } from '../i18n';


interface Section {
  title: string;
  body: string;
}

interface DiagramNode {
  label: string;
  color: string;
  textColor?: string;
}

interface DiagramRow {
  nodes: DiagramNode[];
  arrow?: boolean;
}

interface ConceptCard {
  id: number;
  sequenceItemId?: string;
  title: string;
  subtitle: string;
  color: string;
  icon: string;
  /** Template de projet attachée par l'auteur (CCT) → bouton « Ouvrir le projet » (IDE). */
  projectTemplateId?: number;
  sections: Section[];
  /**
   * A draw.io diagram authored for this card (CCT-477) — full width, under the text.
   *
   * ⚠️ NOT THE SAME THING AS `diagram` BELOW, and the two names are one letter apart on purpose:
   * `diagram` is a boxes-and-arrows figure we draw ourselves from rows of labels, `diagramUrl` is
   * a real .drawio file rendered by the draw.io viewer, with the author's own library shapes.
   */
  diagramUrl?: string;
  /** The author's zoom for that diagram, in percent. */
  diagramZoom?: number;
  diagram?: {
    title: string;
    rows: DiagramRow[];
  };
  isRead: boolean;
  isBookmarked: boolean;
}

type MarkupBlock =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'unordered-list'; items: string[] }
  | { type: 'ordered-list'; items: string[] }
  | { type: 'table'; rows: string[][] }
  | { type: 'blockquote'; text: string }
  | { type: 'code'; language: string; code: string };

// ⚠️ LES FICHES BUNDLEES ONT ETE SUPPRIMEES ICI (CCT-253). Elles servaient de repli quand le
// runtime ne rendait rien : l'screen affichait alors six fiches d'une AUTRE histoire, credibles
// et sans rapport, sans que rien ne le signale. Leurs `sequenceItemId` (`legacy-1`…) ne
// correspondaient a aucun element de la sequence reelle, si bien que la resolution retombait
// sur l'index 0 : « suivant » rebouclait sur la premiere fiche au lieu d'avancer dans la
// mission. Un screen vide QUI SE VOIT vaut mieux qu'un contenu qui ne mene nulle part.

// ⚠️ `t` IS AN ARGUMENT, AND IT IS A useMemo DEPENDENCY AT THE CALL SITE. This helper lives
// outside the component, so it cannot call a hook; and if the memo did not depend on `t`,
// the cards would keep the language in force when they were first built.
function scenarioCardsForMission(
  missionId: number | undefined, chapterId: number | undefined,
  t: (k: MessageKey) => string,
): ConceptCard[] | null {
  if (!missionId) return null;
  const scenario = getScenarioForMission(missionId);
  const chapter = scenario?.chapters?.[(chapterId ?? 1) - 1] ?? scenario?.chapters?.[0];
  if (!chapter) return null;
  // Locked items are shells without content, not empty concept cards.
  const cardItems = chapter.sequence.filter(i => i.type === 'card' && (i as any).locked !== true);
  if (cardItems.length === 0) return null;
  const palette = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'];
  const icons = ['document-text', 'library', 'book', 'layers', 'git-network'];
  return cardItems.map((item, idx) => {
    const body =
      typeof item.content === 'object' && 'body' in item.content
        ? String((item.content as any).body ?? '')
        : '';
    const title =
      typeof item.content === 'object' && 'title' in item.content
        ? String((item.content as any).title ?? item.title)
        : item.title;
    // Template de projet attachée par l'auteur (CCT) : le runtime la sert dans content.
    const projectTemplateId =
      typeof item.content === 'object' && (item.content as any).projectTemplateId
        ? Number((item.content as any).projectTemplateId)
        : undefined;
    return {
      id: idx + 1,
      sequenceItemId: item.id,
      title,
      subtitle: item.title,
      color: palette[idx % palette.length],
      icon: icons[idx % icons.length],
      projectTemplateId,
      sections: [{ title: t('cards.section'), body }],
      // CCT-477 — served by the runtime inside `content`, like `projectTemplateId`.
      diagramUrl:
        typeof item.content === 'object' && (item.content as any).diagramUrl
          ? String((item.content as any).diagramUrl)
          : undefined,
      diagramZoom:
        typeof item.content === 'object' && (item.content as any).diagramZoom
          ? Number((item.content as any).diagramZoom)
          : undefined,
      isRead: false,
      isBookmarked: false,
    } as ConceptCard;
  });
}

export default function MissionCardsScreen({ navigation: navProp, route: routeProp, onProgressConfirmed }: { navigation?: any; route?: any; onProgressConfirmed?: () => void } = {}) {
  const t = useT();
  const C = usePalette();
  const styles = useMemo(() => makeStyles(C), [C]);
  const navigation = navProp ?? useNavigation<any>();
  const route = routeProp ?? useRoute<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { itemId, title } = route.params ?? {};
  const missionId = Number(route.params?.missionId) || undefined;
  const chapterId = Number(route.params?.chapterId) || undefined;
  const scenarioLoad = useMissionScenario(missionId);
  const followsMissionSequence = itemId != null;
  // ⚠️ PLUS DE REPLI SUR LES FICHES BUNDLÉES. Il y en avait un (`?? EGOCRISIS_CARDS`) : quand
  // le runtime n'avait rien, l'écran affichait six fiches d'une AUTRE histoire sans le dire, et
  // aucun de leurs `sequenceItemId` ne correspondait à la séquence réelle — la résolution
  // retombait donc sur l'index 0, ce qui faisait boucler « suivant » sur la première fiche.
  // Mieux vaut un écran vide qui se voit (cf. `getScenarioForMission`, CCT-253).
  const initialCards = useMemo(
    () => scenarioCardsForMission(missionId, chapterId, t) ?? [],
    [missionId, chapterId, t, scenarioLoad.revision],
  );
  // ⚠️ THE SUBTITLE USED TO BE HARD-CODED: every mission that was not 201 or 102 displayed
  // « EgoCrisis — AZ-104 » under this header, whatever the player had actually opened. Same
  // family as the chapter banner. The real title is right here; when there is none, the line
  // is simply ABSENT rather than borrowed from another story.
  const missionScenario = useMemo(
    () => (missionId ? getScenarioForMission(missionId) : undefined),
    [missionId, scenarioLoad.revision],
  );

  const resolveTargetCardIndex = (list: ConceptCard[]) => {
    if (list.length === 0) return 0;
    if (typeof itemId === 'number' && Number.isFinite(itemId) && missionId) {
      const scenario = getScenarioForMission(missionId);
      const chapter = scenario?.chapters?.[(chapterId ?? 1) - 1] ?? scenario?.chapters?.[0];
      if (chapter && itemId >= 1 && itemId <= chapter.sequence.length) {
        const targetSeqItem = chapter.sequence[itemId - 1];
        if (targetSeqItem?.type === 'card') {
          let cardPos = 0;
          for (let i = 0; i < itemId - 1; i += 1) {
            if (chapter.sequence[i]?.type === 'card') cardPos += 1;
          }
          if (cardPos >= 0 && cardPos < list.length) return cardPos;
        }
      }
    }
    if (typeof itemId === 'string' && itemId.length > 0) {
      const bySequenceId = list.findIndex(c => c.sequenceItemId === itemId);
      if (bySequenceId >= 0) return bySequenceId;
    }
    if (typeof title === 'string' && title.length > 0) {
      const byTitle = list.findIndex(c => c.subtitle === title || c.title === title);
      if (byTitle >= 0) return byTitle;
    }
    return 0;
  };

  const [cards, setCards] = useState(initialCards);
  const [currentIndex, setCurrentIndex] = useState(resolveTargetCardIndex(initialCards));
  const currentCard = cards[currentIndex];

  useEffect(() => {
    setCards(initialCards);
    setCurrentIndex(resolveTargetCardIndex(initialCards));
  }, [initialCards, itemId, title]);

  useEffect(() => {
    if (!currentCard) return;
    setCards(prev => prev.map(c => c.id === currentCard.id ? { ...c, isRead: true } : c));
  }, [currentCard?.id]);

  // La navigation ET la progression suivent la fiche AFFICHEE, pas celle d'entree : chaque
  // fiche est un element de sequence a part entiere.
  const itemNav = useVisitedItemNav(route, navigation, currentCard?.sequenceItemId, onProgressConfirmed);
  useElementVisited(missionId, currentCard?.sequenceItemId, !!currentCard);

  const toggleBookmark = (id: number) => {
    setCards(prev => prev.map(c => c.id === id ? { ...c, isBookmarked: !c.isBookmarked } : c));
  };

  const parseMarkupBlocks = (body: string): MarkupBlock[] => {
    const lines = body.replace(/\r\n/g, '\n').split('\n');
    const blocks: MarkupBlock[] = [];
    let i = 0;

    const isBlockBoundary = (line: string) =>
      /^\s*$/.test(line)
      || /^```/.test(line)
      || /^#{1,6}\s+/.test(line)
      || /^>\s?/.test(line)
      || /^\|\s?.+\|\s*$/.test(line)
      || /^-\s+/.test(line)
      || /^\d+\.\s+/.test(line);

    while (i < lines.length) {
      const raw = lines[i] ?? '';
      const line = raw.trim();

      // Bloc de code ```lang … ``` : capture BRUTE (ni markup inline, ni trim des lignes,
      // l'indentation est signifiante). Une fence non refermee s'arrete en fin de contenu.
      const fence = line.match(/^```\s*([A-Za-z0-9+#-]*)\s*$/);
      if (fence) {
        const language = fence[1] || '';
        const buf: string[] = [];
        i++;
        while (i < lines.length && !/^```\s*$/.test((lines[i] ?? '').trim())) {
          buf.push(lines[i] ?? '');
          i++;
        }
        i++;   // consomme la fence fermante
        if (buf.length) blocks.push({ type: 'code', language, code: buf.join('\n').replace(/\s+$/, '') });
        continue;
      }

      if (!line) {
        i += 1;
        continue;
      }

      const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
      if (headingMatch) {
        blocks.push({ type: 'heading', level: headingMatch[1].length, text: headingMatch[2].trim() });
        i += 1;
        continue;
      }

      if (line.startsWith('>')) {
        const quoteLines: string[] = [];
        while (i < lines.length && /^\s*>\s?/.test(lines[i] ?? '')) {
          quoteLines.push((lines[i] ?? '').replace(/^\s*>\s?/, '').trim());
          i += 1;
        }
        blocks.push({ type: 'blockquote', text: quoteLines.join(' ') });
        continue;
      }

      if (/^\|\s?.+\|\s*$/.test(line)) {
        const tableRows: string[][] = [];
        while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i] ?? '')) {
          const row = (lines[i] ?? '')
            .trim()
            .slice(1, -1)
            .split('|')
            .map(cell => cell.trim());
          tableRows.push(row);
          i += 1;
        }
        const cleanedRows = tableRows.filter(r => !r.every(c => /^:?-{3,}:?$/.test(c)));
        if (cleanedRows.length > 0) {
          blocks.push({ type: 'table', rows: cleanedRows });
        }
        continue;
      }

      if (/^-\s+/.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^-\s+/.test((lines[i] ?? '').trim())) {
          items.push((lines[i] ?? '').trim().replace(/^-\s+/, ''));
          i += 1;
        }
        blocks.push({ type: 'unordered-list', items });
        continue;
      }

      if (/^\d+\.\s+/.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^\d+\.\s+/.test((lines[i] ?? '').trim())) {
          items.push((lines[i] ?? '').trim().replace(/^\d+\.\s+/, ''));
          i += 1;
        }
        blocks.push({ type: 'ordered-list', items });
        continue;
      }

      const paragraphLines: string[] = [line];
      i += 1;
      while (i < lines.length && !isBlockBoundary((lines[i] ?? '').trim())) {
        paragraphLines.push((lines[i] ?? '').trim());
        i += 1;
      }
      blocks.push({ type: 'paragraph', text: paragraphLines.join(' ') });
    }

    return blocks;
  };

  const renderInlineMarkup = (text: string, textColor: string) => {
    // ⚠️ THE CARD KEEPS ITS OWN MARKDOWN — it does more than emphasis (italic, code, headings,
    // lists) and a second renderer on top would fight it. Only the em-dash rule is shared, at the
    // entrance, so the character reads the same way here as in a chat or a scene (CCT-389).
    const parts = sansCadratin(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g).filter(Boolean);
    return parts.map((part, idx) => {
      if (/^\*\*[^*]+\*\*$/.test(part)) {
        return (
          <Text key={`b-${idx}`} style={[styles.inlineBold, { color: textColor }]}>
            {part.slice(2, -2)}
          </Text>
        );
      }
      if (/^\*[^*]+\*$/.test(part)) {
        return (
          <Text key={`i-${idx}`} style={[styles.inlineItalic, { color: textColor }]}>
            {part.slice(1, -1)}
          </Text>
        );
      }
      if (/^`[^`]+`$/.test(part)) {
        return (
          <Text key={`c-${idx}`} style={styles.inlineCode}>
            {part.slice(1, -1)}
          </Text>
        );
      }
      return (
        <Text key={`t-${idx}`} style={{ color: textColor }}>
          {part}
        </Text>
      );
    });
  };

  const renderMarkupBody = (body: string, cardColor: string, onOpenProject?: () => void) => {
    const blocks = parseMarkupBlocks(body);
    return blocks.map((block, idx) => {
      if (block.type === 'heading') {
        const headingStyle =
          block.level <= 2 ? styles.mdHeadingLg :
            block.level === 3 ? styles.mdHeadingMd : styles.mdHeadingSm;
        return (
          <Text key={`h-${idx}`} style={[headingStyle, { color: cardColor }]}>
            {renderInlineMarkup(block.text, cardColor)}
          </Text>
        );
      }

      if (block.type === 'paragraph') {
        return (
          <Text key={`p-${idx}`} style={[styles.sectionBody, { color: C.textPrim }]}>
            {renderInlineMarkup(block.text, C.textPrim)}
          </Text>
        );
      }

      if (block.type === 'code') {
        // Chaque bloc de code d'une card portant une template propose « Ouvrir l'IDE » (CCT).
        return <CodeBlock key={`code-${idx}`} code={block.code} language={block.language}
          onOpenProject={onOpenProject} accent={cardColor} />;
      }

      if (block.type === 'blockquote') {
        return (
          <View key={`q-${idx}`} style={[styles.quoteBox, { borderLeftColor: cardColor, backgroundColor: cardColor + '10' }]}>
            <Text style={[styles.quoteText, { color: C.textSec }]}>
              {renderInlineMarkup(block.text, C.textSec)}
            </Text>
          </View>
        );
      }

      if (block.type === 'unordered-list') {
        return (
          <View key={`ul-${idx}`} style={styles.listWrap}>
            {block.items.map((item, itemIdx) => (
              <View key={`ul-item-${itemIdx}`} style={styles.listRow}>
                <Text style={[styles.listBullet, { color: cardColor }]}>•</Text>
                <Text style={[styles.listText, { color: C.textPrim }]}>
                  {renderInlineMarkup(item, C.textPrim)}
                </Text>
              </View>
            ))}
          </View>
        );
      }

      if (block.type === 'ordered-list') {
        return (
          <View key={`ol-${idx}`} style={styles.listWrap}>
            {block.items.map((item, itemIdx) => (
              <View key={`ol-item-${itemIdx}`} style={styles.listRow}>
                <Text style={[styles.listBulletNum, { color: cardColor }]}>{itemIdx + 1}.</Text>
                <Text style={[styles.listText, { color: C.textPrim }]}>
                  {renderInlineMarkup(item, C.textPrim)}
                </Text>
              </View>
            ))}
          </View>
        );
      }

      return (
        <ScrollView key={`t-${idx}`} horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tableWrap}>
          <View style={[styles.table, { borderColor: C.border }]}>
            {block.rows.map((row, rowIdx) => (
              <View key={`row-${rowIdx}`} style={[styles.tableRow, rowIdx > 0 && { borderTopColor: C.border, borderTopWidth: 1 }]}>
                {row.map((cell, cellIdx) => (
                  <View
                    key={`cell-${cellIdx}`}
                    style={[
                      styles.tableCell,
                      cellIdx > 0 && { borderLeftColor: C.border, borderLeftWidth: 1 },
                      rowIdx === 0 && { backgroundColor: cardColor + '16' },
                    ]}
                  >
                    <Text style={[styles.tableCellText, { color: rowIdx === 0 ? cardColor : C.textPrim }]}>
                      {renderInlineMarkup(cell, rowIdx === 0 ? cardColor : C.textPrim)}
                    </Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
        </ScrollView>
      );
    });
  };

  const renderDiagramRow = (row: DiagramRow, rowIdx: number) => {
    if (row.arrow) {
      return (
        <View key={rowIdx} style={styles.diagramArrowRow}>
          <View style={[styles.diagramArrow, { backgroundColor: C.border }]} />
          <View style={[styles.diagramArrowHead, { borderTopColor: C.border }]} />
        </View>
      );
    }
    return (
      <View key={rowIdx} style={styles.diagramRow}>
        {row.nodes.map((node, nIdx) => {
          if (node.color === 'transparent') {
            return <View key={nIdx} style={{ height: 4 }} />;
          }
          return (
            // ⚠️ UN NŒUD EST UN VOILE, PAS UN APLAT. La timeline — et tout le reste de
            // l'application — dit une couleur par un fond teinté et un liseré de la même
            // teinte (`glow`). Ici les nœuds étaient des blocs pleins à texte blanc : la
            // seule surface de l'app à parler cette langue, illisible en thème clair (blanc
            // sur jaune, sur vert clair…) puisque `#FFF` était écrit en dur.
            <View
              key={nIdx}
              style={[styles.diagramNode, glow(node.color, true, isDarkTheme),
                      { flex: 1, marginHorizontal: 3 }]}
            >
              {/* ⚠️ `node.textColor` EST IGNORÉ, ET C'EST VOULU. Il a été choisi par l'auteur
                  pour un aplat plein — typiquement du blanc. Sur un voile clair il disparaît.
                  La couleur du texte se DÉDUIT du fond, comme partout ailleurs. */}
              <Text style={[styles.diagramNodeText,
                            { color: glowText(node.color, true, isDarkTheme) }]}
                    numberOfLines={3}>
                {node.label}
              </Text>
            </View>
          );
        })}
      </View>
    );
  };

  const renderCard = (card: ConceptCard) => {
    // Ouverture du projet dans l'IDE — partagée par le bouton du bas ET chaque bloc de code.
    const openProject = card.projectTemplateId
      ? () => navigation.navigate('CodeIde', {
          projectTemplateId: card.projectTemplateId, missionId,
          itemId: card.sequenceItemId, title: card.title,
        })
      : undefined;
    return (
    <View
      key={card.id}
      style={styles.card}
    >
      <View style={styles.cardHeader}>
        <View style={[styles.cardIconWrap, { backgroundColor: card.color + '22' }]}>
          <Ionicons name={card.icon as any} size={22} color={card.color} />
        </View>
        <View style={styles.cardHeaderText}>
          <View style={styles.cardTitleRow}>
            <Text style={[styles.cardTitle, { color: C.textPrim }]}>{card.title}</Text>
            {!card.isRead && <View style={[styles.unreadDot, { backgroundColor: card.color }]} />}
          </View>
          <Text style={[styles.cardSubtitle, { color: C.textSec }]} numberOfLines={2}>
            {card.subtitle}
          </Text>
        </View>
        <View style={styles.cardHeaderActions}>
          <TouchableOpacity onPress={() => toggleBookmark(card.id)} style={styles.bookmarkBtn}>
            <Ionicons
              name={card.isBookmarked ? 'bookmark' : 'bookmark-outline'}
              size={18}
              color={card.isBookmarked ? card.color : C.textMute}
            />
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.cardBody}>
        {card.sections.map((section, idx) => (
          <View key={idx} style={[styles.section, idx < card.sections.length - 1 && styles.sectionBorder, { borderBottomColor: C.border }]}>
            <Text style={[styles.sectionTitle, { color: card.color }]}>{section.title}</Text>
            <View style={styles.markupContainer}>{renderMarkupBody(section.body, card.color, openProject)}</View>
          </View>
        ))}

        {/* CCT-477 — the authored diagram, FULL WIDTH: it sits outside `styles.section`, which
            indents its content, so the frame runs edge to edge of the card body. A taller frame
            than elsewhere, because a card is read sitting still. */}
        {card.diagramUrl ? (
          <DiagramViewer url={card.diagramUrl} hauteur={320} zoom={card.diagramZoom} />
        ) : null}

        {/* ⚠️ ALIGNÉ SUR LA TIMELINE. Le conteneur portait un couple de gris codé en dur
            (`#1F2937` / `#F9FAFB`) hors de la palette : le diagramme flottait dans un cadre qui
            n'était ni la carte ni la page, et ne suivait aucune correction de palette. */}
        {card.diagram && (
          <View style={[styles.diagramContainer, { backgroundColor: C.card, borderColor: C.border }]}>
            <View style={styles.diagramHeader}>
              <Ionicons name="git-network" size={14} color={card.color} />
              <Text style={[styles.diagramTitle, { color: C.textSec }]}>{card.diagram.title}</Text>
            </View>
            <View style={styles.diagramBody}>
              {card.diagram.rows.map((row, idx) => renderDiagramRow(row, idx))}
            </View>
          </View>
        )}

        {/* Template de projet attachée par l'auteur (CCT) : ouvre l'IDE (même chaîne que l'US
            CodeController). `itemId` = l'id d'élément (`el-…`) pour instancier un projet distinct. */}
        {openProject ? (
          <TouchableOpacity
            style={[styles.openProjectBtn, { borderColor: card.color }]}
            onPress={openProject}
            activeOpacity={0.8}
          >
            <Ionicons name="code-slash" size={16} color={card.color} />
            <Text style={[styles.openProjectText, { color: card.color }]}>{t('cards.openProject')}</Text>
            <Ionicons name="arrow-forward" size={14} color={card.color} />
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
    );
  };

  const readCount = cards.filter(c => c.isRead).length;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: C.bg }]}>
      <ElementHeader
        missionTitle={(route.params as any)?.missionTitle ?? missionScenario?.title ?? ''}
        chapterTitle={(route.params as any)?.chapterTitle ?? ''}
        elementTitle={currentCard?.subtitle ?? (route.params as any)?.title ?? t('cards.title')}
        onBack={() => navigation.goBack()}
        badge={!followsMissionSequence && (
          <View style={[styles.progressBadge, { backgroundColor: C.surface }]}>
            <Text style={[styles.progressText, { color: C.textSec }]}>{readCount}/{cards.length}</Text>
          </View>
        )}
        nav={(
          /* Une fiche ouverte depuis la mission suit tous ses éléments, y compris les US.
             Seule la bibliothèque sans itemId parcourt les fiches entre elles. */
          <HeaderNav
          onPrev={!followsMissionSequence && currentIndex > 0
            ? () => setCurrentIndex((p) => Math.max(0, p - 1))
            : itemNav.goPrev}
          onNext={!followsMissionSequence && currentIndex < cards.length - 1
            ? () => setCurrentIndex((p) => Math.min(cards.length - 1, p + 1))
            : itemNav.goNext}
          accent={TYPE_COLOR.card}
          isLast={followsMissionSequence ? itemNav.isLast : currentIndex >= cards.length - 1 && itemNav.isLast}
          />
        )}
      />

      <ScrollView style={styles.content} contentContainerStyle={styles.contentInner} showsVerticalScrollIndicator={false}>
        {!followsMissionSequence && <View style={[styles.introCard, { backgroundColor: C.cyan + '11', borderColor: C.borderCy }]}>
          <Ionicons name="information-circle" size={18} color={C.cyan} />
          <Text style={[styles.introText, { color: C.textSec }]}>
            {t('cards.oneCardPerScreen')}
          </Text>
        </View>}
        {/* Seul l'INDICATEUR reste : la navigation vit dans les flèches du header, comme sur
            tous les autres éléments (deux jeux de boutons se contredisaient). */}
        {!followsMissionSequence && <View style={styles.paginationRow}>
          <Text style={[styles.pageIndicator, { color: C.textSec }]}>
            {cards.length === 0 ? '0/0' : `${currentIndex + 1}/${cards.length}`}
          </Text>
        </View>}
        {currentCard ? renderCard(currentCard) : (
          <View style={styles.introCard}>
            {scenarioLoad.status === 'loading'
              ? <Text style={{ color: C.textSec }}>{t('chapter.loading')}</Text>
              : <TouchableOpacity onPress={scenarioLoad.retry} accessibilityRole="button">
                  <Text style={{ color: C.textSec }}>{t('common.retry')}</Text>
                </TouchableOpacity>}
          </View>
        )}
      </ScrollView>

    </SafeAreaView>
  );
}

const makeStyles = (C: Palette) => StyleSheet.create({
  container: { flex: 1, minHeight: 0 },
  nextPill: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 22, paddingVertical: 11, borderRadius: 22 },
  nextPillText: { color: C.bg, fontSize: 14, fontWeight: '800', letterSpacing: 0.3 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, gap: 12 },
  backButton: { padding: 8, marginLeft: -4 },
  headerCenter: { flex: 1 },
  headerTitle: { fontSize: 17, fontWeight: '700' },
  headerSubtitle: { fontSize: 12, marginTop: 1 },
  progressBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  progressText: { fontSize: 13, fontWeight: '600' },
  content: { flex: 1 },
  contentInner: { padding: 16 },
  introCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, padding: 12, borderRadius: 10, borderWidth: 1, marginBottom: 16 },
  introText: { flex: 1, fontSize: 13, lineHeight: 18 },
  paginationRow: { alignItems: 'center', marginBottom: 12 },
  pageIndicator: { fontSize: 12, fontWeight: '700' },
  card: {
    borderRadius: 10,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: C.borderCy,
    backgroundColor: C.cyan + '11',
    overflow: 'hidden',
  },
  cardHeader: { flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 },
  cardIconWrap: { width: 44, height: 44, borderRadius: 10, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardHeaderText: { flex: 1 },
  cardTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 3 },
  cardTitle: { fontSize: 16, fontWeight: '800', flex: 1, letterSpacing: 0.2 },
  unreadDot: { width: 8, height: 8, borderRadius: 4 },
  cardSubtitle: { fontSize: 11, lineHeight: 16, letterSpacing: 0.3, textTransform: 'uppercase' as const },
  cardHeaderActions: { flexDirection: 'row', alignItems: 'center', gap: 10, flexShrink: 0 },
  bookmarkBtn: { padding: 4 },
  cardBody: { paddingHorizontal: 16, paddingBottom: 16 },
  openProjectBtn: { marginTop: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 11, borderRadius: 8, borderWidth: 1, backgroundColor: C.surface },
  openProjectText: { fontSize: 13, fontWeight: '700' },
  markupContainer: { gap: 12 },
  section: { paddingVertical: 12 },
  sectionBorder: { borderBottomWidth: 1 },
  sectionTitle: { fontSize: 11, fontWeight: '800', textTransform: 'uppercase' as const, letterSpacing: 1.2, marginBottom: 8 },
  sectionBody: { fontSize: 14, lineHeight: 23 },
  mdHeadingLg: { fontSize: 18, lineHeight: 24, fontWeight: '800', marginTop: 6, marginBottom: 2 },
  mdHeadingMd: { fontSize: 16, lineHeight: 22, fontWeight: '700', marginTop: 4, marginBottom: 2 },
  mdHeadingSm: { fontSize: 14, lineHeight: 20, fontWeight: '700', marginTop: 2, marginBottom: 1 },
  listWrap: { gap: 7 },
  listRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  listBullet: { width: 12, textAlign: 'center', fontSize: 14, lineHeight: 20, fontWeight: '700' },
  listBulletNum: { minWidth: 22, fontSize: 13, lineHeight: 20, fontWeight: '700' },
  listText: { flex: 1, fontSize: 14, lineHeight: 23 },
  quoteBox: { borderLeftWidth: 3, borderRadius: 8, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, paddingVertical: 8, paddingHorizontal: 10 },
  quoteText: { fontSize: 13, lineHeight: 20, fontStyle: 'italic' },
  inlineBold: { fontWeight: '700' },
  inlineItalic: { fontStyle: 'italic' },
  inlineCode: {
    fontFamily: Platform.select({ web: 'ui-monospace, SFMono-Regular, Menlo, monospace', default: 'monospace' }),
    fontSize: 13,
    backgroundColor: C.cyan + '1A',
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 4,
    color: C.cyan,
  },
  tableWrap: { paddingVertical: 2 },
  table: { borderWidth: 1, borderRadius: 8, overflow: 'hidden', minWidth: 420 },
  tableRow: { flexDirection: 'row' },
  tableCell: { flex: 1, paddingHorizontal: 10, paddingVertical: 8, justifyContent: 'center' },
  tableCellText: { fontSize: 12, lineHeight: 18, fontWeight: '600' },
  diagramContainer: { marginTop: 12, borderRadius: 12, borderWidth: 1, padding: 14 },  // 12 = rayon des cartes de la timeline
  diagramHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 12 },
  diagramTitle: { fontSize: 12, fontWeight: '600' },
  diagramBody: { gap: 2 },
  diagramRow: { flexDirection: 'row', alignItems: 'center', marginVertical: 2 },
  diagramNode: { borderRadius: 8, borderWidth: 1, paddingHorizontal: 10, paddingVertical: 8, alignItems: 'center', justifyContent: 'center' },
  diagramNodeText: { fontSize: 11, fontWeight: '600', textAlign: 'center', lineHeight: 15 },
  diagramArrowRow: { alignItems: 'center', height: 20, justifyContent: 'center' },
  diagramArrow: { width: 2, height: 12 },
  diagramArrowHead: { width: 0, height: 0, borderLeftWidth: 6, borderRightWidth: 6, borderTopWidth: 6, borderLeftColor: 'transparent', borderRightColor: 'transparent' },
});
